import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import DocumentUpload, { documentUrl } from "@/components/inventory/DocumentUpload";

const DECISION_LABEL = { accept: "Approved", partial: "Partially approved", reject: "Rejected" };
const DECISION_TONE = {
  accept: "bg-emerald-50 text-emerald-800 border-emerald-200",
  partial: "bg-amber-50 text-amber-800 border-amber-200",
  reject: "bg-rose-50 text-rose-800 border-rose-200",
};

export const DISPOSITIONS = [
  { key: "release", label: "Release to stock", hint: "Material is fine after re-check — make it usable. Must be done by someone other than the inspector who rejected it." },
  { key: "return", label: "Return to vendor", hint: "Send it back — removes the qty from stock." },
  { key: "write_off", label: "Write off / destroy", hint: "Unusable — removes the qty from stock as a loss." },
];
const DISPOSITION_LABEL = Object.fromEntries(DISPOSITIONS.map((d) => [d.key, d.label]));

const HISTORY_LABEL = {
  "qc.accept": "QA approved",
  "qc.partial": "QA partially approved",
  "qc.reject": "QA rejected",
  "ncr.auto": "NCR raised",
  "ncr.capa": "CAPA action added",
  "ncr.close": "NCR closed",
};

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

function Modal({ title, subtitle, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center px-4 py-6 overflow-y-auto" data-testid={testId}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl text-left">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="h-8 px-3 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">Close</button>
        </div>
        <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}

function Photos({ ids }) {
  if (!ids?.length) return <span className="text-xs text-slate-400">No photos</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {ids.map((id, i) => (
        <a key={id} href={documentUrl(id)} target="_blank" rel="noreferrer" title={`Photo ${i + 1}`}
           className="block w-20 h-20 rounded-md border border-slate-200 overflow-hidden bg-slate-50 hover:ring-2 hover:ring-emerald-400">
          <img src={documentUrl(id)} alt={`QA photo ${i + 1}`} className="w-full h-full object-cover" />
        </a>
      ))}
    </div>
  );
}

function historyText(h) {
  const m = h.meta || {};
  if (h.action === "ncr.disposition") {
    return {
      title: `${DISPOSITION_LABEL[m.action] || m.action} — ${m.qty} ${m.uom || ""} ${m.item || ""}`.trim(),
      detail: m.notes,
    };
  }
  if (h.action.startsWith("qc.")) {
    return {
      title: HISTORY_LABEL[h.action],
      detail: [m.accepted_qty != null && `accepted ${m.accepted_qty}`, m.rejected_qty ? `rejected ${m.rejected_qty}` : "", m.notes]
        .filter(Boolean).join(" · "),
    };
  }
  return { title: HISTORY_LABEL[h.action] || h.action, detail: m.kind || "" };
}

function Timeline({ grn, history }) {
  const events = history.filter((h) => h.action !== "grn.create");
  // Older records logged "NCR raised" a moment before the QA decision that caused it — show the decision first.
  const qcAt = events.findIndex((h) => h.action.startsWith("qc."));
  const ncrAt = events.findIndex((h) => h.action === "ncr.auto");
  if (ncrAt >= 0 && qcAt > ncrAt) events.splice(qcAt, 0, ...events.splice(ncrAt, 1));
  const entries = [
    { id: "received", title: "Goods received", detail: grn.po_code ? `Against ${grn.po_code}` : "Direct Stock In", who: grn.created_by_name || grn.created_by_email, at: grn.created_at },
    ...events.map((h) => ({ id: h.id, ...historyText(h), who: h.actor_name || h.actor_email, at: h.timestamp })),
  ];
  return (
    <ol className="relative border-l border-slate-200 ml-1.5 space-y-3" data-testid="qa-history">
      {entries.map((e) => (
        <li key={e.id} className="ml-4">
          <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-emerald-500" />
          <div className="text-sm font-medium text-slate-900">{e.title}</div>
          {e.detail && <div className="text-xs text-slate-600">{e.detail}</div>}
          <div className="text-[11px] text-slate-400">{e.who || "—"} · {fmtDateTime(e.at)}</div>
        </li>
      ))}
    </ol>
  );
}

/** Read-only QA record for an inspected GRN: decision, quantities, photos, NCR outcome and full history. */
export function QARecordDialog({ grnId, ncr, onClose, onOpenNcr }) {
  const [grn, setGrn] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let alive = true;
    Promise.all([api.get(`/inventory/grns/${grnId}`), api.get(`/inventory/grns/${grnId}/history`)])
      .then(([g, h]) => { if (alive) { setGrn(g.data); setHistory(h.data); } })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load QA record"));
    return () => { alive = false; };
  }, [grnId]);

  if (!grn) {
    return <Modal title="QA record" onClose={onClose}><div className="text-sm text-slate-500">Loading…</div></Modal>;
  }

  const rejectedBy = Object.fromEntries((grn.qc_rejected_lines || []).map((l) => [l.stock_id, l]));
  const decidedBy = Object.fromEntries((ncr?.rejected_lines || []).map((l) => [l.stock_id, l.disposition]));
  const held = (grn.stock_refs || []).filter((r) => r.qc_required);
  const decision = grn.qc_decision;

  return (
    <Modal
      title={`QA record — ${grn.code}`}
      subtitle={<>{grn.vendor_name || "—"} · Invoice {grn.invoice_no || "—"}
        {grn.invoice_doc_id && <>{" · "}<a href={documentUrl(grn.invoice_doc_id)} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">view invoice ↗</a></>}
      </>}
      onClose={onClose}
      testId="qa-record"
    >
      <div className={`rounded-md border px-4 py-3 ${DECISION_TONE[decision] || "bg-slate-50 border-slate-200 text-slate-700"}`}>
        <div className="text-sm font-semibold">{DECISION_LABEL[decision] || grn.qc_status}</div>
        <div className="text-xs mt-0.5">by {grn.qc_inspector_email || "—"} · {fmtDateTime(grn.qc_decided_at)}</div>
        {grn.qc_inspector_notes && <div className="text-sm mt-2 whitespace-pre-wrap">{grn.qc_inspector_notes}</div>}
      </div>

      <Section title="Quantities">
        <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
          {held.map((r) => {
            const rej = rejectedBy[r.stock_id];
            const acc = rej ? rej.accepted_qty : r.qty;
            const disp = decidedBy[r.stock_id];
            return (
              <div key={r.stock_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{r.item_name || "Item"}</div>
                  <div className="text-xs text-slate-500">Batch {r.batch_no || "—"} · received {r.qty} {r.uom || ""}</div>
                </div>
                <div className="text-xs text-right tabular-nums">
                  <div className="text-emerald-700">Accepted {acc} {r.uom || ""}</div>
                  {rej && !disp && <div className="text-rose-700">Blocked {rej.rejected_qty} {r.uom || ""}</div>}
                  {rej && disp && (
                    <div className={disp.action === "release" ? "text-emerald-700" : "text-slate-600"}>
                      {DISPOSITION_LABEL[disp.action]} {rej.rejected_qty} {r.uom || ""}
                    </div>
                  )}
                  {rej && disp?.action === "release" && (
                    <div className="text-slate-500 border-t border-slate-100 mt-1 pt-1">Usable {acc + rej.rejected_qty} {r.uom || ""}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Inspection photos"><Photos ids={grn.qc_photo_doc_ids} /></Section>

      {ncr && (
        <Section title="Blocked stock (NCR)">
          <div className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 py-2">
            <div className="text-sm">
              <span className="font-mono text-xs text-slate-700">{ncr.code}</span>{" "}
              <span className="text-slate-600">· {ncr.status === "closed" ? "closed" : `${pendingCount(ncr)} line(s) awaiting decision`}</span>
            </div>
            {onOpenNcr && (
              <button type="button" onClick={() => onOpenNcr(ncr)} className="text-xs font-medium text-emerald-700 hover:text-emerald-900">Open NCR →</button>
            )}
          </div>
        </Section>
      )}

      <Section title="History"><Timeline grn={grn} history={history} /></Section>
    </Modal>
  );
}

export const pendingCount = (ncr) => (ncr.rejected_lines || []).filter((l) => !l.disposition).length;

function DispositionForm({ ncr, lineIndex, onCancel, onDone }) {
  const [action, setAction] = useState("return");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!notes.trim()) {
      toast.error("Enter a reason");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/inventory/ncrs/${ncr.id}/disposition`, {
        line_index: lineIndex, action, notes: notes.trim(), photo_doc_ids: photos.map((p) => p.id),
      });
      toast.success(`${DISPOSITION_LABEL[action]} — recorded on ${ncr.code}`);
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 space-y-3 border-t border-slate-100 pt-3" data-testid={`ncr-disposition-form-${lineIndex}`}>
      <div className="grid gap-2">
        {DISPOSITIONS.map((d) => (
          <label key={d.key} className={`flex gap-3 rounded-md border-2 px-3 py-2 cursor-pointer ${action === d.key ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
            <input type="radio" name={`disp-${lineIndex}`} checked={action === d.key} onChange={() => setAction(d.key)} className="mt-1" data-testid={`ncr-disposition-${d.key}`} />
            <span>
              <span className="block text-sm font-medium text-slate-900">{d.label}</span>
              <span className="block text-xs text-slate-500">{d.hint}</span>
            </span>
          </label>
        ))}
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        placeholder={action === "release" ? "e.g. Re-tested moisture 3.1% — within spec" : action === "return" ? "e.g. Returned via vendor vehicle, debit note DN-12" : "e.g. Contaminated, destroyed in presence of QA"}
        className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
        data-testid="ncr-disposition-notes"
      />
      <div className="space-y-2">
        {photos.map((p) => (
          <DocumentUpload key={p.id} kind="qa_photo" value={p} onChange={() => setPhotos((ps) => ps.filter((x) => x.id !== p.id))} />
        ))}
        <DocumentUpload kind="qa_photo" value={null} onChange={(doc) => doc && setPhotos((ps) => [...ps, doc])} label="Add photo (optional)" />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
        <button type="button" onClick={submit} disabled={submitting}
                className="h-9 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50" data-testid="ncr-disposition-submit">
          {submitting ? "Saving…" : "Save decision"}
        </button>
      </div>
    </div>
  );
}

/** NCR detail: rejected lines with their disposition (release / return / write off), photos and close. */
export function NCRDialog({ ncr: initial, canEdit, onClose, onChanged, onViewGrn }) {
  const [ncr, setNcr] = useState(initial);
  const [deciding, setDeciding] = useState(null);
  const lines = ncr.rejected_lines || [];
  const pending = pendingCount(ncr);

  const close = async () => {
    if (!window.confirm("Close this NCR? All CAPA actions should be completed first.")) return;
    try {
      const { data } = await api.post(`/inventory/ncrs/${ncr.id}/close`);
      setNcr(data);
      toast.success("NCR closed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Modal
      title={`${ncr.code} — ${ncr.title}`}
      subtitle={<>{ncr.vendor_name || "—"} · raised by {ncr.created_by_email || "—"} · {fmtDateTime(ncr.created_at)}
        {onViewGrn && ncr.grn_id && <>{" · "}<button type="button" onClick={() => onViewGrn(ncr.grn_id)} className="text-emerald-700 hover:underline">QA record ↗</button></>}
      </>}
      onClose={onClose}
      testId="ncr-dialog"
    >
      {ncr.description && <div className="text-sm text-slate-700 whitespace-pre-wrap">{ncr.description}</div>}

      <Section title="Blocked stock">
        {lines.length === 0 && <div className="text-xs text-slate-400">No line details on this NCR.</div>}
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="border border-slate-200 rounded-md px-3 py-2" data-testid={`ncr-line-${i}`}>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{l.item_name || "Item"}</div>
                  <div className="text-xs text-slate-500">Batch {l.batch_no || "—"} · blocked {l.rejected_qty} {l.uom || ""}</div>
                </div>
                {l.disposition ? (
                  <span className="text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700">
                    {DISPOSITION_LABEL[l.disposition.action]}
                  </span>
                ) : canEdit && ncr.status === "open" && deciding !== i ? (
                  <button type="button" onClick={() => setDeciding(i)} className="text-xs font-medium text-emerald-700 hover:text-emerald-900" data-testid={`ncr-decide-${i}`}>
                    Decide →
                  </button>
                ) : (
                  <span className="text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-1 rounded bg-amber-50 text-amber-700">Pending</span>
                )}
              </div>
              {l.disposition && (
                <div className="mt-2 text-xs text-slate-600 space-y-2">
                  <div>{l.disposition.notes}</div>
                  <div className="text-[11px] text-slate-400">{l.disposition.by_name || l.disposition.by_email} · {fmtDateTime(l.disposition.at)}</div>
                  {l.disposition.photo_doc_ids?.length > 0 && <Photos ids={l.disposition.photo_doc_ids} />}
                </div>
              )}
              {deciding === i && (
                <DispositionForm
                  ncr={ncr}
                  lineIndex={i}
                  onCancel={() => setDeciding(null)}
                  onDone={(updated) => { setNcr(updated); setDeciding(null); onChanged(); }}
                />
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Inspection photos"><Photos ids={ncr.photo_doc_ids} /></Section>

      {canEdit && ncr.status === "open" && (
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
          {pending > 0 && <span className="text-xs text-slate-500">Decide all blocked lines before closing.</span>}
          <button type="button" onClick={close} disabled={pending > 0}
                  className="h-9 px-4 rounded-md border border-emerald-300 bg-white text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-40" data-testid="ncr-close">
            Close NCR
          </button>
        </div>
      )}
    </Modal>
  );
}
